import { Injectable } from '@angular/core';
import { DotNetBridge, ISettingInfo, SettingInfo } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LocalSettingsService } from '../../../../local-settings.service';
import { ILoginSettings, LoginSetting, LoginSettings } from './../models/login-settings.model';
import { AuthConfigService } from './auth-config.service';

@Injectable({
  providedIn: 'root',
})
export class LoginSettingsService extends LocalSettingsService<ILoginSettings, LoginSettings, LoginSetting> {
  constructor(abcConfigService: AbcConfigService, protected authConfigService: AuthConfigService) {
    super(abcConfigService, LoginSettings, LoginSetting);
  }

  protected get settingsCatalogueStorageKeyName(): string {
    return 'LoginSettings';
  }

  protected get settingInfos(): ReadonlyMap<LoginSetting, ISettingInfo> {
    return LoginSettingsService._SettingInfos;
  }

  public get hasCurrentLoginSettings(): Observable<boolean> {
    return this.getCurrentLoginSettings().pipe(map((loginSettings) => loginSettings != null));
  }

  public get usesLocalSecurityContext(): Observable<boolean> {
    return this.authConfigService.current.pipe(map((cfg) => cfg?.useLocalSecurityContext));
  }

  public static readonly HTTP_HEADER_AUTH_TOKEN: string = 'Authorization';

  private static readonly _SettingInfos: ReadonlyMap<LoginSetting, ISettingInfo> = new Map<LoginSetting, ISettingInfo>([
    [LoginSetting.access_token, { settingName: 'access_token', responsePropertyName: 'access_token' }],
    [LoginSetting.refresh_token, { settingName: 'refresh_token', responsePropertyName: 'refresh_token' }],
    [LoginSetting.token_type, { settingName: 'token_type', responsePropertyName: 'token_type' }],
    [LoginSetting.expires, { settingName: 'expires', responsePropertyName: '.expires', settingValueType: Date }],
    [
      LoginSetting.expires_in,
      {
        settingName: 'expires_in',
        responsePropertyName: 'expires_in',
        settingValueType: Number,
      },
    ],
    [LoginSetting.issued, { settingName: 'issued', responsePropertyName: '.issued', settingValueType: Date }],
    [LoginSetting.userName, { settingName: 'userName', responsePropertyName: 'userName' }],
  ]);

  protected postProcessSettingsAfterRead(settings: LoginSettings): Observable<LoginSettings> {
    if (settings?.userName) {
      sessionStorage.setItem('userName', settings.userName);
    } else {
      sessionStorage.removeItem('userName');
    }
    return super.postProcessSettingsAfterRead(settings);
  }

  protected preProcessSettingsBeforeSave(settings: LoginSettings): Observable<LoginSettings> {
    return super.preProcessSettingsBeforeSave(settings).pipe(
      map((newSettings) => {
        if (!newSettings?.isLoginRequired) {
          newSettings = null;
        }

        // TODO @todo Remove as soon direct storage values are not necessary anymore
        if (newSettings) {
          for (const settingInfo of Array.from(this.settingInfos.values())) {
            const safeSettingInfo: SettingInfo = new SettingInfo(settingInfo);
            if (settingInfo.settingName === 'access_token') {
              const settingValue = newSettings[safeSettingInfo.settingName];
              const settingTextValue: string = safeSettingInfo.getTextFromSettingValue(settingValue);

              this.saveTextValueInStorage(safeSettingInfo.settingName, settingTextValue);
            } else {
              this.saveTextValueInStorage(safeSettingInfo.settingName, undefined);
            }
          }
        } else {
          this.deleteAllSettingsFromStorage();
        }

        return newSettings;
      })
    );
  }

  public createLoginSettingsFromLoginResponse(loginResponse: any): ILoginSettings {
    if (!loginResponse || loginResponse.length === 0) {
      throw new Error('Login Response is empty.');
    }

    return this.createSettingsFromResponseItem(loginResponse);
  }

  public getCurrentLoginSettings(): Observable<ILoginSettings> {
    return this.authConfigService.current.pipe(
      concatMap((cfg) => {
        if (cfg && cfg.useLocalSecurityContext) {
          return of(<ILoginSettings>LoginSettings.CreateAnonymousLoginSettings());
        } else if (cfg && !cfg.needsAuthentication) {
          return this.readSettingsFromStorage().pipe(
            concatMap((_) => {
              if (_) {
                return of(_);
              } else {
                return of(<ILoginSettings>LoginSettings.CreateAnonymousLoginSettings());
              }
            }),
            catchError(() => of(<ILoginSettings>LoginSettings.CreateAnonymousLoginSettings()))
          );
        } else if (DotNetBridge.hasContext) {
          return from(this.getDotNetContextLoginSettings());
        } else {
          return this.readSettingsFromStorage();
        }
      })
    );
  }

  private async getDotNetContextLoginSettings(): Promise<ILoginSettings> {
    const s = new LoginSettings();
    s.userName = await DotNetBridge.context.get('UserName');
    s.access_token = await DotNetBridge.context.get('AccessToken');
    return s;
  }

  public setCurrentLoginSettings(loginSettings: ILoginSettings): Observable<void> {
    return this.writeSettingsToStorage(loginSettings);
  }

  public removeCurrentLoginSettings(): Observable<void> {
    return this.setCurrentLoginSettings(null);
  }

  // TODO @todo Remove as soon direct storage values are not necessary anymore
  private deleteAllSettingsFromStorage(): void {
    // TODO: With --downlevelIteration compiler option:
    // for (let setting of LocalAuthSettingsService.LoginSettingInfos.keys()) {
    for (const settingInfo of Array.from(this.settingInfos.values())) {
      const safeSettingInfo: SettingInfo = new SettingInfo(settingInfo);
      const storageKeyName = safeSettingInfo.settingName;

      sessionStorage.removeItem(storageKeyName);
      localStorage.removeItem(storageKeyName);
    }
  }

  // TODO @todo Remove as soon direct storage values are not necessary anymore
  // private getTextValueFromStorage(storageKeyName: string): string {
  //   return sessionStorage.getItem(storageKeyName) ?? localStorage.getItem(storageKeyName);
  // }

  // TODO @todo Remove as soon direct storage values are not necessary anymore
  private saveTextValueInStorage(storageKeyName: string, storageTextValue: string): void {
    if (storageTextValue) {
      sessionStorage.setItem(storageKeyName, storageTextValue);
      localStorage.setItem(storageKeyName, storageTextValue);
    } else {
      sessionStorage.removeItem(storageKeyName);
      localStorage.removeItem(storageKeyName);
    }
  }
}
